import { useEffect, useRef, useState } from 'react';
import Peer from 'peerjs';
import {
  remotePushState,
  remotePollInbox,
  remoteAckInbox,
} from '../utils/remoteApi';

export function useRemoteRelay({ enabled, roomId, operatorSecret, getSnapshotRef, onCommandsRef }) {
  const lastAckRef = useRef(0);
  const peerRef = useRef(null);
  const connsRef = useRef({});
  const lastPushedJsonRef = useRef('');
  const [peerCount, setPeerCount] = useState(0);
  const broadcastRef = useRef(null);

  useEffect(() => {
    if (!enabled || !roomId || !operatorSecret) return undefined;
    lastAckRef.current = 0;
    lastPushedJsonRef.current = '';
    let cancelled = false;

    const peerId = `dp-${roomId}`;
    const iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];
    const peer = new Peer(peerId, { config: { iceServers } });
    peerRef.current = peer;

    peer.on('open', (id) => {
      console.log('[PeerJS:console] registered as', id);
    });

    peer.on('error', (err) => {
      console.warn('[PeerJS:console] error', err.type, err.message);
      if (err.type === 'unavailable-id') {
        console.warn('[PeerJS:console] peer ID already taken — another console tab open?');
      }
    });

    function recountPeers() {
      const count = Object.values(connsRef.current).filter((c) => c.open).length;
      setPeerCount(count);
    }

    /** One map entry per logical remote (`metadata.clientId`); PeerJS `conn.peer` changes on reconnect. */
    function connMapKey(c) {
      const cid = c.metadata?.clientId;
      if (cid != null && String(cid).trim() !== '') return `c:${String(cid)}`;
      return `p:${c.peer}`;
    }

    peer.on('connection', (conn) => {
      const remoteLabel = conn.metadata?.clientId || conn.peer;
      console.log('[PeerJS:console] incoming connection from', remoteLabel);

      conn.on('open', () => {
        console.log('[PeerJS:console] DataConnection OPEN from', remoteLabel);
        const key = connMapKey(conn);
        const prev = connsRef.current[key];
        if (prev && prev !== conn) {
          try {
            prev.close();
          } catch {
            /* ignore */
          }
          delete connsRef.current[key];
        }
        connsRef.current[key] = conn;
        recountPeers();
      });

      conn.on('data', (raw) => {
        try {
          const msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (msg.type === 'command' && msg.action && onCommandsRef.current) {
            onCommandsRef.current([{ id: Date.now(), action: msg.action }]);
          }
        } catch { /* ignore */ }
      });

      conn.on('close', () => {
        console.log('[PeerJS:console] connection closed from', remoteLabel);
        const key = connMapKey(conn);
        if (connsRef.current[key] === conn) delete connsRef.current[key];
        recountPeers();
      });

      conn.on('error', (err) => {
        console.warn('[PeerJS:console] connection error from', remoteLabel, err);
        const key = connMapKey(conn);
        if (connsRef.current[key] === conn) delete connsRef.current[key];
        recountPeers();
      });
    });

    function broadcastMsg(msgStr) {
      let sentToAny = false;
      for (const [id, conn] of Object.entries(connsRef.current)) {
        if (conn.open) {
          try { conn.send(msgStr); sentToAny = true; } catch {
            delete connsRef.current[id];
          }
        }
      }
      return sentToAny;
    }

    function broadcastState(snap) {
      return broadcastMsg(JSON.stringify({ type: 'state', data: snap }));
    }

    broadcastRef.current = (type, data) => {
      broadcastMsg(JSON.stringify({ type, ...(data || {}) }));
    };

    const pushTimer = window.setInterval(() => {
      if (cancelled) return;
      const snap = getSnapshotRef.current?.();
      if (!snap) return;

      broadcastState(snap);

      // Always mirror snapshot to the Durable Object when it changes. Phones may still show
      // `peerConnected` while the data channel is dead (iOS sleep / background); HTTP display
      // poll is their fallback and must stay fresh even when P2P looks healthy.
      const snapJson = JSON.stringify(snap);
      if (snapJson !== lastPushedJsonRef.current) {
        lastPushedJsonRef.current = snapJson;
        remotePushState(roomId, operatorSecret, snap).catch(() => {});
      }
    }, 100);

    // Always poll the command inbox: remotes often fall back to HTTP while the console still
    // has a stale PeerJS connection that looks "open", which previously suppressed polling.
    const pollTimer = window.setInterval(() => {
      if (cancelled) return;
      (async () => {
        try {
          const { commands } = await remotePollInbox(roomId, operatorSecret, lastAckRef.current);
          if (commands?.length && onCommandsRef.current) {
            const maxId = onCommandsRef.current(commands);
            if (typeof maxId === 'number' && maxId > lastAckRef.current) {
              lastAckRef.current = maxId;
              await remoteAckInbox(roomId, operatorSecret, maxId);
            }
          }
        } catch { /* transient */ }
      })();
    }, 70);

    return () => {
      cancelled = true;
      broadcastRef.current = null;
      clearInterval(pushTimer);
      clearInterval(pollTimer);
      for (const conn of Object.values(connsRef.current)) {
        try { conn.close(); } catch { /* ignore */ }
      }
      connsRef.current = {};
      setPeerCount(0);
      if (peerRef.current) {
        try { peerRef.current.destroy(); } catch { /* ignore */ }
        peerRef.current = null;
      }
    };
  }, [enabled, roomId, operatorSecret, getSnapshotRef, onCommandsRef]);

  const broadcastSessionEnd = useRef(() => broadcastRef.current?.('session_ended'));

  const disconnectPeerRef = useRef(null);
  disconnectPeerRef.current = (clientId) => {
    const msg = JSON.stringify({ type: 'session_ended' });
    for (const [mapKey, conn] of Object.entries(connsRef.current)) {
      if (conn.metadata?.clientId !== clientId) continue;
      if (conn.open) {
        try {
          conn.send(msg);
        } catch {
          /* ignore */
        }
      }
      setTimeout(() => {
        try {
          conn.close();
        } catch {
          /* ignore */
        }
        if (connsRef.current[mapKey] === conn) delete connsRef.current[mapKey];
        setPeerCount(Object.values(connsRef.current).filter((c) => c.open).length);
      }, 150);
    }
  };

  const sendApprovalRef = useRef(null);
  sendApprovalRef.current = (clientId, remoteToken) => {
    const msg = JSON.stringify({ type: 'approved', remoteToken });
    for (const conn of Object.values(connsRef.current)) {
      if (conn.metadata?.clientId === clientId && conn.open) {
        try { conn.send(msg); } catch { /* ignore */ }
      }
    }
  };

  return { peerCount, broadcastSessionEnd, disconnectPeer: disconnectPeerRef, sendApproval: sendApprovalRef };
}
